/**
 * §3.6 Checkout — pay-obligation CTA technical shell (SHIPPING OFF).
 * Copy slot + confirm affordance before payment start.
 * BAN invent LEGAL obligation prose — neutral i18n keys only.
 * NOT shippingLightEnabled-gated. Wave0 CMS ≠ this surface.
 * G12 — seller policy-link binaries live in-shell (Terms/privacy/returns/withdrawal).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from 'pi-kiosk-shared/ui';
import { Button } from '../../../shared/ui/Button.js';
import { SellerPolicyStrip } from '../../mor-chrome/SellerPolicyStrip.js';

const COPY_ID = 'customer-pay-obligation-copy';
const CONFIRM_LABEL_ID = 'customer-pay-obligation-confirm-label';

export type PayObligationCtaShellProps = {
  /** Controlled confirm; omit for uncontrolled internal state. */
  readonly confirmed?: boolean;
  readonly onConfirmedChange?: (confirmed: boolean) => void;
  /** When set with onConfirm, renders the payment-start CTA gated on confirm. */
  readonly ctaLabel?: string;
  readonly ctaDisabled?: boolean;
  readonly ctaPending?: boolean;
  readonly onConfirm?: () => void;
  /**
   * FIXEDTOTAL from CheckoutSessionResponse.amountMinor + currency
   * (tax-inclusive MoR payable — fees/discounts already in session total).
   */
  readonly amountMinor?: number;
  readonly currency?: string;
  /** When true with amount, show incl-VAT + fees/discounts honesty line. */
  readonly showVatHonesty?: boolean;
  /**
   * Tenant code for in-shell seller policy links (Terms/privacy/returns/withdrawal).
   * When omitted, policy strip is skipped (confirm-only / donation surfaces).
   */
  readonly apiTenantCode?: string | null;
  /** Show language toggle inside the in-shell policy strip. */
  readonly showPolicyLanguageToggle?: boolean;
};

function formatCheckoutAmount(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  const formatted = Number.isInteger(major) ? major.toString() : major.toFixed(2);
  return `${formatted} ${currency}`;
}

export function PayObligationCtaShell({
  confirmed: confirmedControlled,
  onConfirmedChange,
  ctaLabel,
  ctaDisabled = false,
  ctaPending = false,
  onConfirm,
  amountMinor,
  currency,
  showVatHonesty = false,
  apiTenantCode = null,
  showPolicyLanguageToggle = false,
}: PayObligationCtaShellProps): JSX.Element {
  const { t } = useTranslation('customer');
  const [confirmedUncontrolled, setConfirmedUncontrolled] = useState(false);
  const isControlled = confirmedControlled !== undefined;
  const confirmed = isControlled ? confirmedControlled : confirmedUncontrolled;

  const setConfirmed = (next: boolean): void => {
    if (!isControlled) {
      setConfirmedUncontrolled(next);
    }
    onConfirmedChange?.(next);
  };

  const showCta = ctaLabel != null && onConfirm != null;
  const showAmount =
    amountMinor != null && currency != null && currency.trim().length > 0;
  const ctaBlocked = !confirmed || ctaDisabled || ctaPending;
  const tenantForPolicies =
    apiTenantCode != null && apiTenantCode.trim().length > 0
      ? apiTenantCode.trim()
      : null;

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="customer-pay-obligation-shell"
    >
      <p
        id={COPY_ID}
        className="text-sm text-[var(--color-on-surface)]"
        data-testid="customer-pay-obligation-copy"
      >
        {t('checkout.payObligation.copySlot')}
      </p>
      {tenantForPolicies != null ? (
        <SellerPolicyStrip
          apiTenantCode={tenantForPolicies}
          testIdPrefix="customer-pay-obligation-policies"
          showLanguageToggle={showPolicyLanguageToggle}
        />
      ) : null}
      <div className="flex items-start justify-between gap-3 rounded-md border border-[var(--color-border)] px-3 py-3">
        <p
          id={CONFIRM_LABEL_ID}
          className="min-w-0 flex-1 text-sm font-medium text-[var(--color-on-surface)]"
        >
          {t('checkout.payObligation.confirmLabel')}
        </p>
        <Switch
          checked={confirmed}
          onCheckedChange={setConfirmed}
          aria-labelledby={CONFIRM_LABEL_ID}
          aria-describedby={COPY_ID}
          data-testid="customer-pay-obligation-confirm"
          className="mt-0.5 shrink-0"
        />
      </div>
      {showCta ? (
        <div className="flex flex-col gap-3 border-t border-[var(--color-border)] pt-4">
          {showAmount ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-3">
                <span className="text-base font-medium text-[var(--color-on-surface)]">
                  {t('payment.totalDue')}
                </span>
                <span
                  className="text-xl font-bold text-[var(--color-on-surface)]"
                  data-testid="customer-pay-obligation-amount"
                >
                  {formatCheckoutAmount(amountMinor, currency)}
                </span>
              </div>
              {showVatHonesty ? (
                <p
                  className="m-0 text-xs text-[var(--color-on-surface-muted)]"
                  data-testid="customer-pay-obligation-vat-honesty"
                >
                  {t('morChrome.vat.checkoutHonesty')}
                </p>
              ) : null}
            </div>
          ) : null}
          <Button
            intent="primary"
            size="lg"
            block
            type="button"
            data-testid="customer-payment-pay-cta"
            disabled={ctaBlocked}
            onClick={onConfirm}
          >
            {ctaLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
